import { inject, Injectable } from '@angular/core';
import { EventBusEvents } from '@siza-staff-care/event-bus-constants';
import {
	AppStateService,
	buildFlattenedSecurityMap,
	FlatMenuItem,
	RawSecurityMap,
	SecurityConfigStackEntry,
} from '@siza-staff-care/shared/app-state';
import { KeyValuesPair } from '@siza-staff-care/shared/data';
import { typedClone } from '@siza-staff-care/shared/utils';
import { NgEventBus } from 'ng-event-bus';
import { addSecurityItemsToSecurityMap } from './menu-routes/security-builders';
import { SessionTimeoutService } from './session-timeout.service';
import { securityMappings } from './menu-routes/security-mappings';

const KEYS = {
	AUTH_MODE: 'authMode',
	CONFIG: 'config',
};

@Injectable({
	providedIn: 'root',
})
export class SessionService {
	private _favouritesConfig?: Record<string, FlatMenuItem>;
	orchestrationItems: string[] = [];
	staffMasterOid?: string;

	private sessionTimeout = inject(SessionTimeoutService);
	private eventBus = inject(NgEventBus);
	private appState = inject(AppStateService);

	// authMode
	get authMode(): string {
		return this.getSessionStorageItem(KEYS.AUTH_MODE);
	}

	set authMode(value: string) {
		this.setSessionStorageItem(KEYS.AUTH_MODE, value);
	}

	// config
	get config() {
		return this.getSessionStorageItem(KEYS.CONFIG);
	}

	set config(value) {
		this.setSessionStorageItem(KEYS.CONFIG, value);
	}

	get favouritesConfig() {
		if (!this._favouritesConfig || !Object.keys(this._favouritesConfig).length) {
			this._favouritesConfig = this.buildFavouritesConfig(this.appState.flatMenuItems);
		}

		return this._favouritesConfig;
	}

	//
	// Methods
	//

	start(asRenew: boolean) {
		// Ensure session is ended before starting a new one.
		this.end();

		if (!asRenew) {
			this.sessionTimeout.startSession();
		}

		this.eventBus.cast(EventBusEvents.Event_Session_Start_End, null);
	}

	end() {
		this.orchestrationItems = [];
		delete this._favouritesConfig;
		this.sessionTimeout.endSession();

		this._favouritesConfig = undefined;

		this.eventBus.cast(EventBusEvents.Event_Session_Start_End, null);
	}

	setOrchestrationItems(items: string[]) {
		items = items.map((i) => i.replace('WShiftControl', securityMappings.ShiftControl));
		this.orchestrationItems = items || [];
	}

	findFavouritesMenuItemForScreen(screenName: string) {
		if (!this.favouritesConfig) {
			return;
		}

		return this.favouritesConfig[screenName];
	}

	pushWorkflowConfigSecurity(formAndControlItems: KeyValuesPair[]) {
		const securityMap = this.appState.securityMap;
		const editorMap = this.appState.editorMap;

		const oldEntry: SecurityConfigStackEntry = {
			securityMap,
			editorMap,
			flattenedSecurityMap: this.appState.flattenedSecurityMap,
		};

		const edMap = typedClone(editorMap);

		let secMap = typedClone(securityMap);
		secMap = addSecurityItemsToSecurityMap(secMap, formAndControlItems);

		const entry: SecurityConfigStackEntry = {
			securityMap: secMap,
			editorMap: edMap,
			flattenedSecurityMap: buildFlattenedSecurityMap(secMap as RawSecurityMap),
		};

		this.appState.pushSecurityConfig([oldEntry, entry]);
	}

	popWorkflowConfigSecurity() {
		this.appState.popSecurityConfig();
	}

	private buildFavouritesConfig(items: FlatMenuItem[] | undefined) {
		items = items || [];

		const favouritesConfig = items.reduce(
			(acc, item) => {
				acc[item.key] = item;
				return acc;
			},
			{} as Record<string, FlatMenuItem>,
		);

		return favouritesConfig;
	}

	private getSessionStorageItem(key: string): string {
		return sessionStorage.getItem(key) || '';
	}

	private setSessionStorageItem(key: string, value: unknown) {
		const toSet = typeof value === 'string' ? (value as string) : JSON.stringify(value);
		sessionStorage.setItem(key, toSet);
	}
}
